import { ActionItemStatus } from "../../types/actionItems";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useIsMobile } from "../../hooks/use-mobile";
import { Badge } from "../ui/badge";
import React from "react";

interface StatusFilterProps {
  value: ActionItemStatus | 'all' | 'open';
  onChange: (status: ActionItemStatus | 'all' | 'open') => void;
  counts: {
    all: number;
    todo: number;
    in_progress: number;
    on_hold: number;
    done: number;
  };
}

export function StatusFilter({ value = 'open', onChange, counts }: StatusFilterProps) {
  const isMobile = useIsMobile();

  // Calculate the count for "Open Items"
  const openCount = counts.todo + counts.in_progress + counts.on_hold;

  // Place "Open Items" as the first filter
  const statuses = [
    { value: 'open', label: 'Open Items', count: openCount },
    { value: 'todo', label: 'To Do', count: counts.todo },
    { value: 'in_progress', label: 'In Progress', count: counts.in_progress },
    { value: 'on_hold', label: 'On Hold', count: counts.on_hold },
    { value: 'done', label: 'Done', count: counts.done },
    { value: 'all', label: 'All', count: counts.all },
  ];

  if (isMobile) {
    return (
      <Select
        value={value}
        onValueChange={(newValue: string) => onChange(newValue as ActionItemStatus | 'all' | 'open')}
      >
        <SelectTrigger className="w-full">
          <SelectValue>
            {statuses.find(status => status.value === value)?.label || 'Open Items'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {statuses.map((status) => (
            <SelectItem key={status.value} value={status.value}>
              <div className="flex items-center gap-2">
                <span>{status.label}</span>
                <Badge variant="secondary" className="ml-auto">
                  {status.count}
                </Badge>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Tabs
      value={value}
      onValueChange={(newValue: string) => onChange(newValue as ActionItemStatus | 'all' | 'open')}
      className="w-full"
    >
      <TabsList className="w-full justify-start gap-2 h-auto bg-transparent border-b border-gray-200">
        {statuses.map((status) => (
          <TabsTrigger
            key={status.value}
            value={status.value}
            className="relative px-4 py-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-blue-600 after:absolute after:bottom-[-1px] after:left-0 after:h-0.5 after:w-full after:scale-x-0 data-[state=active]:after:bg-blue-600 data-[state=active]:after:scale-x-100 after:transition-transform"
          >
            <span>{status.label}</span>
            <Badge variant="secondary" className="ml-2">
              {status.count}
            </Badge>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}